import Decimal from 'decimal.js';
import * as yup from 'yup';

import { UserRequirement } from 'apis/restaurant-api/merchant-admin/menu2/types';
import getMaxCurrencyDecimals from 'modules/menu/components/getCurrencyMaxDecimals';

const yupTestMonetaryDecimals = {
  name: 'monetaryDecimals',
  test: (value: number | undefined, context: yup.TestContext<{ currency: Currency }>) => {
    const decimals = context.options.context?.currency ? getMaxCurrencyDecimals(context.options.context.currency) : 2;

    if (value !== undefined && new Decimal(value).decimalPlaces() > decimals) {
      return context.createError({
        message:
          decimals === 0
            ? { id: 'validation.mustBeInteger' }
            : { id: 'validation.atMostNDecimals', values: { decimals } },
      });
    }
    return true;
  },
};

export const bulkUpdatePeriodSpecialsValidationSchema = yup.object({
  previousAvailabilityEnd: yup.number().required(),
  availabilityEnd: yup
    .number()
    .min(new Date().getTime())
    .when(
      '$greatestAvailabilityPeriodStart',
      (greatestAvailabilityPeriodStart: number | undefined, schema: yup.NumberSchema) =>
        !greatestAvailabilityPeriodStart
          ? schema
          : schema.moreThan(greatestAvailabilityPeriodStart, { id: 'validation.mustBeGreaterThanAllDateStart' }),
    )
    .required(),
});

export const bulkCreateSpecialsValidationSchema = yup.object({
  discount: yup
    .number()
    .min(0)
    .when('discountType', (discountType: 'absolute' | 'relative-percentage' | 'none', schema: yup.NumberSchema) => {
      if (discountType === 'relative-percentage') {
        return schema.max(100).integer().required();
      }

      if (discountType === 'absolute') {
        return schema.test(yupTestMonetaryDecimals).required();
      }

      return schema;
    }),
  discountType: yup
    .mixed<'absolute' | 'relative-percentage' | 'none'>()
    .oneOf(['absolute', 'relative-percentage', 'none'])
    .required(),
  availabilityStart: yup.number(),
  availabilityEnd: yup
    .number()
    .when('availabilityStart', (availabilityStart: number | undefined, schema: yup.NumberSchema) => {
      if (!!availabilityStart) {
        return schema.moreThan(availabilityStart, { id: 'validation.mustBeEndAfterStart' });
      } else {
        return schema;
      }
    }),
  userRequirement: yup.mixed<UserRequirement>(),
});

export type ValidBatchCreateSpecialsFormValues = yup.Asserts<typeof bulkCreateSpecialsValidationSchema>;
export type ValidBatchUpdateSpecialsAvailabilityPeriodFormValues = yup.Asserts<
  typeof bulkUpdatePeriodSpecialsValidationSchema
>;
